import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  BackgroundColorProps,
  BorderRadiusProps,
  BoxShadowProps,
  ColorProps,
  DisplayProps,
  FlexProps,
  MaxHeightProps,
  PositionProps,
  SizeProps,
  WidthProps,
  ZIndexProps,
} from 'styled-system';
import {
  alignItems,
  backgroundColor,
  borderRadius,
  boxShadow,
  color,
  display,
  flex,
  maxHeight,
  position,
  size,
  space,
  width,
  zIndex,
} from 'styled-system';

import { Button } from 'common/components/Button';
import Icon from 'common/components/Icon';
import { HeadlineH5 } from 'common/components/Typography';
import { DRAWER_CLOSE_BUTTON_Z_INDEX } from 'common/constants/z-index';
import { useLocalization } from 'common/providers/LocaleProvider';
import { textWithEllipsis } from 'common/theme/styles';
import type { SpaceProps } from 'common/theme/system-types';
import type {
  BoxSizingProps,
  CursorProps,
  ThemeHeightProps,
  ThemeLeftProps,
  ThemeRightProps,
  ThemeWidthProps,
} from 'common/theme/system-utilities';
import {
  boxSizing,
  cursor,
  themeHeight,
  themeLeft,
  themeRight,
  themeWidth,
} from 'common/theme/system-utilities';

interface HeaderProps {
  onClick: () => void;
  title?: string | React.ReactNode;
  id?: string;
  withBackButton?: boolean;
  backButtonAriaLabel?: string;
  withCloseDisabled?: boolean;
}

const ICON_SIZE = 32;

const HeaderSection = styled.div<
  AlignItemsProps &
    BackgroundColorProps &
    BoxShadowProps &
    BoxSizingProps &
    DisplayProps &
    SpaceProps &
    ThemeHeightProps &
    ThemeWidthProps &
    WidthProps &
    ZIndexProps
>`
  ${alignItems};
  ${backgroundColor};
  ${boxShadow};
  ${boxSizing};
  ${color};
  ${display};
  ${space};
  ${themeHeight};
  ${themeWidth};
  ${width};
  ${zIndex};
`;

const headerSectionStyles: ComponentProps<typeof HeaderSection> = {
  alignItems: 'center',
  backgroundColor: 'sys.neutral.background.default',
  boxShadow: 'bottom',
  boxSizing: 'border-box',
  color: 'sys.neutral.text.strong',
  display: 'flex',
  px: 'sp_20',
  py: 'sp_0',
  themeHeight: ['54px', 'sp_64'],
  themeWidth: '100%',
  width: 1,
  zIndex: DRAWER_CLOSE_BUTTON_Z_INDEX,
};

const CloseIcon = styled(Icon)<SizeProps & SpaceProps & CursorProps & ColorProps>`
  ${size};
  ${space};
  ${cursor};
  ${color};
`;

const closeIconStyle: Partial<ComponentProps<typeof CloseIcon>> = {
  size: ICON_SIZE,
  color: 'sys.neutral.icon.default',
};

const CloseButton = styled(Button)<
  CursorProps & PositionProps & ThemeRightProps & SpaceProps & BorderRadiusProps & ThemeLeftProps
>`
  ${cursor};
  ${position};
  ${themeRight};
  ${themeLeft};
  ${borderRadius};
`;

const buttonStyles = (
  themeSide: 'themeRight' | 'themeLeft'
): ComponentProps<typeof CloseButton> => ({
  cursor: 'pointer',
  kind: 'bare',
  position: 'absolute',
  [themeSide]: 'sp_8',
  p: 'sp_8',
  borderRadius: 'circle',
});

const Title = styled(HeadlineH5)<MaxHeightProps & FlexProps>`
  ${maxHeight};
  ${flex};
  ${textWithEllipsis()};
`;

const titleStyle: ComponentProps<typeof Title> = {
  textAlign: 'center',
  mr: 'sp_32',
  as: 'h1',
  flex: 1,
};

const Header = ({
  onClick,
  title,
  id,
  withBackButton,
  backButtonAriaLabel,
  withCloseDisabled,
}: HeaderProps) => {
  const { t } = useLocalization();
  const text = title ?? t('h24_important_info');

  return (
    <HeaderSection {...headerSectionStyles}>
      <Title id={id} data-testid="drawer-header-title" {...titleStyle}>
        {text}
      </Title>
      {!withCloseDisabled && (
        <CloseButton
          {...buttonStyles(withBackButton ? 'themeLeft' : 'themeRight')}
          data-testid={withBackButton ? 'back-button' : 'close-button'}
          aria-label={backButtonAriaLabel ? backButtonAriaLabel : t('h24_drawer_close_button')}
          onClick={onClick}
        >
          <CloseIcon {...closeIconStyle} name={withBackButton ? 'arrowLeft' : 'close'} />
        </CloseButton>
      )}
    </HeaderSection>
  );
};

export default Header;
